import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Star, Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/LangContext";

// Loads the book's quiz (null while loading or when the book has none).
export const useQuiz = (bookId, enabled) => {
  const [quiz, setQuiz] = useState(null);
  useEffect(() => {
    setQuiz(null);
    if (!enabled || !bookId) return;
    api.get(`/books/${bookId}/quiz`).then((r) => setQuiz(Array.isArray(r.data?.questions) && r.data.questions.length ? r.data : null)).catch(() => setQuiz(null));
  }, [bookId, enabled]);
  return [quiz, setQuiz];
};

const Stars = ({ n }) => (
  <span className="inline-flex gap-1" data-testid="quiz-result-stars" aria-label={`${n}/3`}>
    {[0, 1, 2].map((i) => <Star key={i} className={`w-8 h-8 ${i < n ? "fill-[#FBBF24] text-[#F59E0B]" : "text-stone-300"}`} />)}
  </span>
);

const optionLabels = (q, tq) => (q.type === "tf" ? [tq.true, tq.false] : q.options);

const Choice = ({ label, picked, state, onClick, testid }) => {
  const tone = state === "right" ? "border-[#047857] bg-emerald-50 text-[#065F46]"
    : state === "wrong" ? "border-red-700 bg-red-50 text-red-900"
      : picked ? "border-[#1C1917] bg-[#1C1917] text-white" : "border-black/15 bg-white hover:border-[#1C1917]";
  return (
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`w-full text-start rounded-2xl border-2 px-4 py-3 font-semibold transition-colors flex items-center gap-2 ${tone}`}>
      {state === "right" && <CheckCircle2 className="w-5 h-5 shrink-0" />}
      {state === "wrong" && <XCircle className="w-5 h-5 shrink-0" />}
      <span dir="auto">{label}</span>
    </button>
  );
};

const QuestionCard = ({ q, i, value, onPick, result }) => {
  const { t } = useLang();
  const tq = t.quiz;
  const right = result ? result.right_answers?.[String(i)] : undefined;
  const isWrong = result && right !== undefined;
  return (
    <li className="bg-white/70 border border-black/10 rounded-2xl p-4 sm:p-5" data-testid={`quiz-question-${i}`}>
      <p className="font-bold mb-3" dir="auto"><span className="text-[#8B3A2B]">{i + 1}.</span> {q.text}</p>
      <div className={`grid gap-2 ${q.type === "tf" ? "grid-cols-2" : "sm:grid-cols-2"}`}>
        {optionLabels(q, tq).map((label, k) => {
          let state;
          if (result) {
            if (k === value) state = isWrong ? "wrong" : "right";
            else if (isWrong && k === right) state = "right";
          }
          return <Choice key={k} label={label} picked={value === k} state={state} onClick={result ? undefined : () => onPick(k)} testid={`quiz-option-${i}-${k}`} />;
        })}
      </div>
      {isWrong && <p className="mt-2 text-sm font-semibold text-[#065F46]" data-testid={`quiz-correct-${i}`} dir="auto">{tq.correctIs}: {optionLabels(q, tq)[right]}</p>}
    </li>
  );
};

// The student answers once; the site marks it at once and shows stars and the right answers for mistakes.
export const ReadingQuiz = ({ bookId, quiz, onDone }) => {
  const { t } = useLang();
  const tq = t.quiz;
  const questions = quiz?.questions ?? [];
  const [answers, setAnswers] = useState(() => questions.map(() => null));
  const [sending, setSending] = useState(false);
  const result = quiz?.attempt ?? null;
  const shown = result ? result.answers : answers;
  const missing = answers.filter((a) => a === null).length;

  const submit = async () => {
    if (missing) { toast.error(tq.answerAll(missing)); return; }
    setSending(true);
    try {
      const r = await api.post(`/books/${bookId}/quiz/submit`, { answers });
      onDone({ ...quiz, attempt: r.data });
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div className="max-w-3xl mx-auto py-2" data-testid="reading-quiz">
      <h2 className="font-display text-2xl sm:text-3xl font-bold mb-1">{tq.title}</h2>
      <p className="text-sm opacity-80 mb-5">{result ? tq.doneNote : tq.note}</p>
      {result && (
        <div className="mb-6 rounded-2xl bg-[#FFFBEB] border-2 border-[#F59E0B] p-5 flex flex-wrap items-center gap-4 text-[#1C1917]" data-testid="quiz-result">
          <Stars n={result.stars} />
          <div>
            <p className="text-2xl font-black" data-testid="quiz-result-score">{tq.score(result.score, result.total)}</p>
            <p className="text-sm">{result.stars ? tq.starsWon(result.stars) : tq.noStars}</p>
          </div>
        </div>
      )}
      <ol className="space-y-4">
        {questions.map((q, i) => (
          <QuestionCard key={i} q={q} i={i} value={shown?.[i] ?? null} result={result}
            onPick={(k) => setAnswers((a) => a.map((v, j) => (j === i ? k : v)))} />
        ))}
      </ol>
      {!result && (
        <button onClick={submit} disabled={sending} data-testid="quiz-submit-btn"
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#047857] text-white px-8 py-3 font-bold hover:bg-[#065F46] disabled:opacity-60 transition-colors">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} {tq.submit}
        </button>
      )}
    </div>
  );
};
